
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { createCustomerPortalSession, getSubscriptionStatus } from '@/services/stripeService';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const CustomerPortal = () => {
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);

  useEffect(() => {
    const loadSubscription = async () => {
      const data = await getSubscriptionStatus();
      setSubscription(data);
      setLoading(false);
    };
    
    loadSubscription();
  }, []);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const url = await createCustomerPortalSession();
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error accessing customer portal:', error);
    }
    setPortalLoading(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!subscription.isSubscribed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Active Subscription</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>You don't have an active subscription</AlertTitle>
            <AlertDescription>
              Subscribe to access premium content and support your favorite journalists.
            </AlertDescription>
          </Alert>
          <Button 
            onClick={() => window.location.href = "/subscription?tab=plans"}
            className="w-full sm:w-auto"
          >
            View Subscription Plans
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your Subscription</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border rounded-lg">
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Subscription Plan</h3>
            <p className="font-medium text-lg">
              {subscription.details?.product_name || "Premium Plan"}
            </p>
          </div>
          
          <div className="p-4 border rounded-lg">
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Billing Period</h3>
            <p className="font-medium text-lg">
              {subscription.details?.interval || "Monthly"}
            </p>
          </div>
          
          <div className="p-4 border rounded-lg">
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Status</h3>
            <div className="flex items-center">
              <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
              <p className="font-medium text-lg">Active</p>
            </div>
          </div>
        </div>
        
        <Button 
          onClick={handleManageSubscription} 
          disabled={portalLoading}
          className="w-full sm:w-auto"
        >
          {portalLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Manage Subscription
          <ExternalLink className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default CustomerPortal;
